<template>
  <!-- Media -->
  <div
    class="cm-media-card-base"
    :class="{
      'has-modal': item.media_type === 'media' ? item.video?.filename : item.youtube_video?.[0].play_in_fullscreen,
    }"
    @click="handleVideoPlay"
    @mouseenter="setHoverTrue"
    @mouseleave="setHoverFalse">
    <!-- Wave -->
    <UtilWave v-if="item.wave?.length" v-editable="item.wave[0]" :blok="item.wave[0]" />

    <div
      class="media-wrapper"
      :class="{ 'is-wide': mediaView === 'wide' }"
      :style="mediaView !== 'wide' ? { 'aspect-ratio': item.aspect_ratio?.replace('-', '/') } : undefined">
      <UtilYoutubeVideo
        v-if="isYoutubeVideo && item.youtube_video?.[0]"
        :blok="item.youtube_video[0]"
        :privacyWarningRichtext="youtubePrivacyWarning"
        :isDark="!item.is_dark"
        class="media" />
      <template v-if="isYoutubeVideo === false">
        <div v-if="item.video_loop?.filename">
          <SharedBaseVideo
            class="media"
            :src="item.video_loop.filename"
            :poster="item.image?.filename"
            :autoplay="true"
            :loop="true"
            :posterBreakPointsWidthMap="breakpointsWidthMap" />
        </div>
        <SharedBaseImage
          v-else-if="item.image?.filename"
          class="media"
          :image="item.image"
          :breakpointsWidthMap="breakpointsWidthMap"
          :heightRatio="mediaView === 'wide' ? (breakpointState.isTablet ? [21, 9] : [1, 1]) : imageAspectRatio" />
      </template>
    </div>

    <!-- Play button for modal video -->
    <UtilButton
      v-if="isYoutubeVideo === false && item.video?.filename"
      :blok="{
        component: 'util_button',
        size: 'large',
        theme: 'secondary',
        _uid: 'play-button',
        text: '',
      }"
      forceIcon="fi_play"
      :aria-label="t('global.playVideo')"
      :isHovered="isHovered"
      :isBackgroundDark="!item.is_dark"
      class="play-button"
      @click="handleVideoPlay" />
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from '#imports';
import { computed, ref } from 'vue';
import { SharedBaseImage, SharedBaseVideo } from '@seb-platform/shared/components/base';
import { useBreakpoint, useModalVideo } from '@seb-platform/shared/composables';
import UtilButton from '~/components/storyblok/utils/UtilButton/UtilButton.vue';
import UtilYoutubeVideo from '~/components/storyblok/utils/UtilYoutubeVideo/UtilYoutubeVideo.vue';
import type { CmMediaItemStoryblok, RichtextStoryblok } from '~/types/storyblok-generated';
import UtilWave from '../../utils/UtilWave/UtilWave.vue';

const props = withDefaults(
  defineProps<{
    item: Omit<CmMediaItemStoryblok, 'aspect_ratio'> & {
      aspect_ratio?: CmMediaItemStoryblok['aspect_ratio'] | '21-9'; // '21-9' cannot be set in Storyblok, only needs to be available for the wide view variant of the card
    };
    mediaView?: 'normal' | 'wide' | 'align-right' | 'align-left' | 'horizontal';
    youtubePrivacyWarning: RichtextStoryblok;
    breakpointsWidthMap?: Record<number, number>;
  }>(),
  { mediaView: 'normal', aspectRatio: '3-2', breakpointsWidthMap: () => ({ 0: 700 }) }
);

const { state: stateModalVideo } = useModalVideo();
const { state: breakpointState } = useBreakpoint();
const { t } = useI18n();

const isHovered = ref(false);
const isYoutubeVideo = props.item.media_type === 'youtube-video';

const imageAspectRatio = computed<[number, number]>(() =>
  props.item.aspect_ratio ? (props.item.aspect_ratio?.split('-').map(n => parseInt(n)) as [number, number]) : [3, 2]
);

function setHoverTrue() {
  if (isYoutubeVideo) {
    return;
  }
  isHovered.value = true;
}
function setHoverFalse() {
  if (isYoutubeVideo) {
    return;
  }
  isHovered.value = false;
}

function handleVideoPlay() {
  if (isYoutubeVideo) {
    // Youtube video is handled in UtilYoutubeVideo
    return;
  }
  if (props.item.media_type === 'media' && props.item.video?.filename) {
    stateModalVideo.value = { type: 'media', filename: props.item.video?.filename };
    return;
  }
}
</script>

<style src="./CMMediaCardBase.scss" lang="scss" scoped />
